// Online payment forms of the public payment page (legacy views/payment.vue).
//
// - PayPal_Pro / PayPal_Rest / AuthorizeNetApi_Api: card form → POST app/pay/{gateway}/{hash}
// - PayPal Standard (PAYPAL_ENABLED): link to {moduleURL}/paypal/{hash}
// - Stripe needs Stripe.js card elements (token) which the React admin does not
//   bundle; the page shows a notice instead (documented limitation).

import { useState, type ChangeEvent } from 'react';
import { Alert, Box, Button, Divider, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { Card as CardIcon } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, moduleRootUrl } from '../../api/client';
import { t } from '../../i18n';
import { str, toNumber, type AnyRecord } from './format';
import { useBootData } from './storeSettings';

const CARD_GATEWAYS: Record<string, string> = {
  PayPal_Rest: 'PayPal Rest',
  PayPal_Pro: 'PayPal Pro',
  AuthorizeNetApi_Api: 'Authorize.net'
};

type CardForm = {
  firstName: string;
  lastName: string;
  billingAddress1: string;
  billingCity: string;
  billingPostcode: string;
  billingState: string;
  billingCountry: string;
  number: string;
  expiry: string;
  cvv: string;
};

const emptyCard: CardForm = { firstName: '', lastName: '', billingAddress1: '', billingCity: '', billingPostcode: '', billingState: '', billingCountry: '', number: '', expiry: '', cvv: '' };

export function PublicPaymentForms({ payment, onPaid }: { payment: AnyRecord; onPaid: (payment: AnyRecord) => void }) {
  const boot = useBootData();
  const gateway = str(boot.payment.gateway);
  const paypal = boot.payment.paypal === true || ['1', 'true'].includes(str(boot.payment.paypal).toLowerCase());
  const [form, setForm] = useState<CardForm>(emptyCard);
  const [errors, setErrors] = useState<Partial<Record<keyof CardForm, string>>>({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error' | 'info'; message: string } | null>(null);

  if (toNumber(payment.received) === 1 || toNumber(payment.review) === 1) return null;
  const cardGateway = CARD_GATEWAYS[gateway];
  if (!cardGateway && !paypal && gateway !== 'Stripe') return null;

  const set = (key: keyof CardForm) => (event: ChangeEvent<HTMLInputElement>) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const validate = () => {
    const next: Partial<Record<keyof CardForm, string>> = {};
    (Object.keys(emptyCard) as Array<keyof CardForm>).forEach((key) => {
      if (!form[key].trim()) next[key] = t('doc.pay.required');
    });
    if (form.number && form.number.replace(/\s+/g, '').length !== 16) next.number = t('doc.pay.cardNumberError');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    setNotice(null);
    const [year, month] = form.expiry.split('-');
    try {
      const response = await adminApi.post(`pay/${gateway}/${payment.hash}`, {
        firstName: form.firstName,
        lastName: form.lastName,
        billingAddress1: form.billingAddress1,
        billingCity: form.billingCity,
        billingPostcode: form.billingPostcode,
        billingState: form.billingState,
        billingCountry: form.billingCountry,
        number: form.number.replace(/\s+/g, ''),
        expiryMonth: Number(month),
        expiryYear: Number(year),
        cvv: form.cvv
      });
      const data = response.data as { success?: boolean; redirect?: string; payment?: AnyRecord; message?: string };
      if (data.success && data.redirect) {
        setNotice({ severity: 'info', message: t('doc.pay.redirecting') });
        window.location.href = data.redirect;
      } else if (data.success) {
        setNotice({ severity: 'success', message: t('doc.pay.charged') });
        if (data.payment) onPaid(data.payment);
      } else {
        setNotice({ severity: 'error', message: data.message ?? t('common.actionFailed') });
      }
    } catch (error) {
      setNotice({ severity: 'error', message: apiErrorMessage(error, t('common.actionFailed')) });
    } finally {
      setSaving(false);
    }
  };

  const moduleUrl = str(boot.payment.moduleURL) || moduleRootUrl();
  const field = (key: keyof CardForm, label: string, size: number, type = 'text') => (
    <Grid size={{ xs: 12, md: size }}>
      <TextField
        fullWidth
        size="small"
        type={type}
        label={label}
        value={form[key]}
        onChange={set(key)}
        error={Boolean(errors[key])}
        helperText={errors[key]}
        InputLabelProps={type === 'month' ? { shrink: true } : undefined}
      />
    </Grid>
  );

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }} className="vd-no-print">
      <Stack spacing={2}>
        {notice ? <Alert severity={notice.severity}>{notice.message}</Alert> : null}
        {cardGateway ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              <CardIcon size={18} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('doc.pay.withCard')} ({cardGateway})
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {field('firstName', t('doc.pay.firstName'), 6)}
              {field('lastName', t('doc.pay.lastName'), 6)}
              {field('billingAddress1', t('doc.pay.billingAddress'), 12)}
              {field('billingCity', t('doc.pay.billingCity'), 6)}
              {field('billingPostcode', t('doc.pay.billingPostcode'), 6)}
              {field('billingState', t('doc.pay.billingState'), 6)}
              {field('billingCountry', t('doc.pay.billingCountry'), 6)}
              {field('number', t('doc.pay.cardNumber'), 6)}
              {field('expiry', t('doc.pay.expiryDate'), 3, 'month')}
              {field('cvv', t('doc.pay.cvv'), 3)}
            </Grid>
            <Button variant="contained" size="large" disabled={saving} onClick={() => void submit()}>
              {saving ? t('doc.pay.processing') : t('doc.pay.pay')}
            </Button>
          </>
        ) : null}
        {gateway === 'Stripe' ? <Alert severity="info">{t('doc.pay.stripeUnavailable')}</Alert> : null}
        {paypal ? (
          <Box>
            {cardGateway ? <Divider sx={{ mb: 2 }} /> : null}
            <Button variant="outlined" size="large" fullWidth href={`${moduleUrl}/paypal/${payment.hash}`}>
              {t('doc.pay.payWithPaypal')}
            </Button>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  );
}
